/**
 * Module quản lý và điều khiển YouTube IFrame Player API (Core Engine)
 * Đóng gói toàn bộ logic phát, dừng, tua, đổi tốc độ và đồng bộ thời gian
 * IFrame được ngụy trang ẩn bên dưới lớp giao diện dựng phim (CapCut / DaVinci / Canva)
 */

export class VideoPlayerEngine {
  constructor() {
    this.player = null;
    this.isReady = false;
    this.isPlaying = false;
    this.currentVideoId = null;
    this.playbackRate = 1.0;
    this.volume = 100;
    this.isMuted = false;

    // Hàng đợi lưu video được yêu cầu phát khi player chưa khởi tạo xong
    this.pendingVideo = null;

    // Đối tượng YouTube Player phụ phục vụ hiệu ứng ánh sáng môi trường Ambient Light (Double-Player)
    this.ambientPlayer = null;
    this.isAmbientReady = false;
    this.currentAmbientContainerId = null;

    // Trạng thái bật/tắt Ambient Light (mặc định Bật khi chạy trên trình duyệt có localStorage)
    this.isAmbientEnabled = false;
    try {
      if (typeof localStorage !== 'undefined') {
        this.isAmbientEnabled = localStorage.getItem('fake_video_ambient_light') !== 'false';
      }
    } catch (e) {
      this.isAmbientEnabled = false;
    }

    // Bộ lắng nghe sự kiện từ bên ngoài (Timeline, UI Controller, Inspector, Ambient)
    this.listeners = {
      timeUpdate: [],
      stateChange: [],
      ready: [],
      error: [],
      loading: [],
      loaded: [],
      ambientChange: []
    };

    // Vòng lặp cập nhật thời gian thời gian thực (Ticker)
    this.tickerInterval = null;
  }

  /**
   * Chuyển đổi mã lỗi của YouTube Player thành thông báo tiếng Việt dễ hiểu
   * @param {number} errorCode 
   * @returns {string}
   */
  getErrorMessage(errorCode) {
    switch (errorCode) {
      case 2:
        return 'ID video không hợp lệ hoặc sai định dạng.';
      case 5:
        return 'Lỗi trình phát HTML5 trên trình duyệt.';
      case 100:
        return 'Không tìm thấy video. Video này có thể đã bị xóa hoặc đặt ở chế độ riêng tư.';
      case 101:
      case 150:
        return 'Chủ kênh video đã chặn quyền phát nhúng trên trang web ngoài. Vui lòng chọn một video khác!';
      default:
        return `Đã xảy ra lỗi khi tải video (Mã lỗi YouTube: ${errorCode}). Vui lòng thử lại!`;
    }
  }

  /**
   * Đăng ký lắng nghe sự kiện
   * @param {'timeUpdate' | 'stateChange' | 'ready' | 'error' | 'loading' | 'loaded'} event 
   * @param {Function} callback 
   */
  on(event, callback) {
    if (this.listeners[event] && typeof callback === 'function') {
      this.listeners[event].push(callback);
    }
  }

  /**
   * Phát sự kiện tới toàn bộ người nghe đã đăng ký
   * @param {string} event 
   * @param {any} data 
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(cb => {
        try {
          cb(data);
        } catch (e) {
          console.error(`Lỗi thực thi callback sự kiện ${event}:`, e);
        }
      });
    }
  }

  /**
   * Nạp YouTube IFrame API từ CDN chính thức của Google
   * Có cơ chế cache Promise tránh ghi đè callback onYouTubeIframeAPIReady khi gọi đồng thời
   * @returns {Promise<void>}
   */
  loadYouTubeApi() {
    // 1. Nếu API đã sẵn sàng trên window thì hoàn tất ngay lập tức
    if (typeof window !== 'undefined' && window.YT && window.YT.Player) {
      return Promise.resolve();
    }

    // 2. Nếu đang có tiến trình nạp dở, tái sử dụng Promise tránh xung đột callback
    if (this.ytApiPromise) {
      return this.ytApiPromise;
    }

    this.ytApiPromise = new Promise((resolve) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      if (window.YT && window.YT.Player) {
        resolve();
        return;
      }

      // Đăng ký callback khi script tải xong
      const prevOnYouTubeIframeAPIReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prevOnYouTubeIframeAPIReady === 'function') {
          try { prevOnYouTubeIframeAPIReady(); } catch (e) {}
        }
        resolve();
      };

      // Nếu chưa có thẻ script trong DOM thì chèn vào
      if (!document.getElementById('yt-iframe-api-script')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api-script';
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        if (firstScriptTag && firstScriptTag.parentNode) {
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
          document.head.appendChild(tag);
        }
      }
    });

    return this.ytApiPromise;
  }

  /**
   * Khởi tạo đối tượng YT.Player vào container DOM được chỉ định
   * @param {string} containerId ID phần tử chứa player
   * @param {string} [initialVideoId] Mã video ban đầu (nếu có)
   * @returns {Promise<void>}
   */
  /**
   * Khởi tạo đối tượng YT.Player vào container DOM được chỉ định
   * @param {string} containerId ID phần tử chứa player
   * @param {string} [initialVideoId] Mã video ban đầu
   * @param {number} [startSeconds=0] Giây bắt đầu
   * @param {boolean} [autoPlay=false] Tự động phát
   * @returns {Promise<void>}
   */
  async initPlayer(containerId = 'cc-player-iframe', initialVideoId = '', startSeconds = 0, autoPlay = false) {
    if (!initialVideoId) {
      // Chưa có videoId để nạp -> Không tạo player rỗng, tránh treo onReady
      this.currentContainerId = containerId;
      return;
    }

    await this.loadYouTubeApi();

    const requestedTime = Number(startSeconds);
    const initialStartSeconds = Number.isFinite(requestedTime) ? Math.max(0, requestedTime) : 0;
    this.currentVideoId = initialVideoId;

    return new Promise((resolve) => {
      // Nếu player đã tồn tại và sẵn sàng
      if (this.player && this.isReady) {
        this.loadVideo(initialVideoId, startSeconds, autoPlay);
        resolve();
        return;
      }

      // Timeout an toàn 5s nếu YouTube không phản hồi onReady
      const readyTimeout = setTimeout(() => {
        console.warn('[VideoEngine] YouTube Player onReady timeout sau 5s, fallback resolve');
        this.isReady = true;
        this.emit('loaded', { videoId: initialVideoId });
        resolve();
      }, 5000);

      try {
        this.currentContainerId = containerId;
        this.player = new window.YT.Player(containerId, {
          height: '100%',
          width: '100%',
          videoId: initialVideoId,
          playerVars: {
            autoplay: autoPlay ? 1 : 0,
            start: Math.floor(initialStartSeconds),
            controls: 0,            // Ẩn thanh điều khiển mặc định của YouTube
            disablekb: 1,           // Vô hiệu hóa phím tắt mặc định YouTube
            fs: 0,                  // Tắt nút Fullscreen YouTube
            modestbranding: 1,      // Giảm thiểu tối đa nhận diện thương hiệu YouTube
            rel: 0,                 // Không hiện gợi ý video liên quan
            iv_load_policy: 3,      // Tắt chú thích trên video
            cc_load_policy: 0,      // Tắt phụ đề mặc định
            cc_lang_pref: 'none',   // Không tải trước bất kỳ ngôn ngữ phụ đề nào
            playsinline: 1
          },
          events: {
            onReady: (event) => {
              clearTimeout(readyTimeout);
              this.isReady = true;
              this.emit('ready', event);
              // Metadata có thể tới sau onReady, kể cả khi autoplay bị chặn.
              this.startTicker();
              this.broadcastTime();

              // Tắt phụ đề ngay khi player đã sẵn sàng
              this.disableSubtitles();

              // Nếu có video đang xếp hàng chờ nạp
              if (this.pendingVideo) {
                const { videoId, startSeconds, autoPlay } = this.pendingVideo;
                this.pendingVideo = null;
                this.loadVideo(videoId, startSeconds, autoPlay);
              } else {
                // Khôi phục vị trí cả khi autoplay bị trình duyệt chặn.
                try {
                  if (initialStartSeconds > 0) {
                    event.target.seekTo(initialStartSeconds, true);
                  }
                  if (autoPlay) event.target.playVideo();
                } catch (e) {}
              }

              // Khởi tạo player phụ Ambient Light nếu tính năng đang bật
              if (this.isAmbientEnabled && typeof localStorage !== 'undefined') {
                const ambientId = this.getAmbientContainerId(containerId);
                this.initAmbientPlayer(ambientId, initialVideoId, initialStartSeconds, autoPlay);
              }

              resolve();
            },
            onStateChange: (event) => {
              this.handleStateChange(event.data);
            },
            onError: (event) => {
              clearTimeout(readyTimeout);
              const errorCode = event.data;
              const message = this.getErrorMessage(errorCode);
              console.warn(`Lỗi YouTube Player [Mã ${errorCode}]:`, message);
              this.emit('error', {
                errorCode,
                message,
                videoId: this.currentVideoId
              });
              resolve();
            }
          }
        });
      } catch (err) {
        clearTimeout(readyTimeout);
        console.warn('Lỗi khi khởi tạo YT.Player:', err);
        resolve();
      }
    });
  }

  /**
   * Xử lý trạng thái phát của YouTube Player
   * YT.PlayerState: -1 (UNSTARTED), 0 (ENDED), 1 (PLAYING), 2 (PAUSED), 3 (BUFFERING), 5 (CUED)
   * @param {number} stateCode 
   */
  handleStateChange(stateCode) {
    if (stateCode === 1) { // Đang phát
      if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
      this.isPlaying = true;
      this.startTicker();
      this.emit('loaded', { videoId: this.currentVideoId, stateCode });

      // Khi video bắt đầu phát, chủ động gỡ bỏ module phụ đề nhiều lần (đề phòng module nạp chậm)
      this.disableSubtitles();
      setTimeout(() => this.disableSubtitles(), 500);
      setTimeout(() => this.disableSubtitles(), 1500);
    } else if (stateCode === 2 || stateCode === 5 || stateCode === 0) { // Tạm dừng, đã nạp xong hoặc kết thúc
      if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
      this.isPlaying = false;
      if (stateCode === 0) this.stopTicker();
      else this.startTicker();
      this.emit('loaded', { videoId: this.currentVideoId, stateCode });
      this.broadcastTime();
    } else if (stateCode === 3) { // Đang đệm (Buffering)
      // Video đang tải thêm dữ liệu đệm
    } else { // -1 (UNSTARTED)
      this.isPlaying = false;
      if (this.isReady) this.startTicker();
    }

    this.emit('stateChange', {
      stateCode,
      isPlaying: this.isPlaying
    });
  }

  /**
   * Bắt đầu vòng lặp đồng bộ thời gian (mỗi 100ms)
   */
  startTicker() {
    this.stopTicker();
    this.tickerInterval = setInterval(() => {
      this.broadcastTime();
    }, 100);
  }

  /**
   * Dừng vòng lặp đồng bộ thời gian
   */
  stopTicker() {
    if (this.tickerInterval) {
      clearInterval(this.tickerInterval);
      this.tickerInterval = null;
    }
  }

  /**
   * Tính toán và phát sự kiện tiến độ video tới timeline
   */
  broadcastTime() {
    if (!this.player || !this.isReady) return;

    try {
      const currentTime = this.player.getCurrentTime ? this.player.getCurrentTime() : 0;
      const duration = this.player.getDuration ? this.player.getDuration() : 0;
      const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

      this.emit('timeUpdate', {
        currentTime: currentTime || 0,
        duration: duration || 0,
        progressPercent: Math.min(100, Math.max(0, progressPercent))
      });

      // Tự động đồng bộ lại thời gian cho Ambient Player nếu bị lệch quá 0.8s
      if (this.ambientPlayer && this.isAmbientEnabled && this.isPlaying && typeof this.ambientPlayer.getCurrentTime === 'function') {
        try {
          const ambientTime = this.ambientPlayer.getCurrentTime() || 0;
          if (Math.abs(ambientTime - (currentTime || 0)) > 0.8) {
            this.ambientPlayer.seekTo(currentTime || 0, true);
          }
        } catch (e) {}
      }
    } catch (e) {
      // Bỏ qua lỗi khi player đang re-render
    }
  }

  /**
   * Nạp và phát một video mới
   * @param {string} videoId Mã video YouTube
   * @param {number} [startSeconds=0] Thời điểm bắt đầu (giây)
   * @param {boolean} [autoPlay=true] Tự động phát ngay
   */
  async loadVideo(videoId, startSeconds = 0, autoPlay = true) {
    if (!videoId || typeof videoId !== 'string' || !videoId.trim()) {
      console.warn('loadVideo bị bỏ qua: videoId không hợp lệ');
      return;
    }

    const cleanVideoId = videoId.trim();
    this.currentVideoId = cleanVideoId;

    // Phát sự kiện bắt đầu tải để UI hiển thị spinner/loading overlay
    this.emit('loading', { videoId: cleanVideoId });

    // Fallback Timer: Tối đa 6 giây nếu không nhận được loaded thì tự động ẩn loading spinner
    if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
    this.loadingTimeout = setTimeout(() => {
      if (this.player && typeof this.player.playVideo === 'function') {
        try { this.player.playVideo(); } catch (e) {}
      }
      this.emit('loaded', { videoId: cleanVideoId, fallback: true });
    }, 6000);

    // Xác định container đang hoạt động trên giao diện
    const activeCanva = document.getElementById('view-canva')?.classList.contains('active');
    const targetContainerId = activeCanva ? 'cv-player-iframe' : 'cc-player-iframe';

    // Nếu Player chưa khởi tạo hoặc chưa ready: Khởi tạo ngay lập tức vào container mục tiêu
    if (!this.player || !this.isReady) {
      console.log(`[VideoEngine] Khởi tạo mới player với video: ${cleanVideoId} tại #${targetContainerId}`);
      await this.mountTo(targetContainerId, cleanVideoId, startSeconds, autoPlay);
      return;
    }

    try {
      if (autoPlay) {
        if (typeof this.player.loadVideoById === 'function') {
          this.player.loadVideoById({
            videoId: cleanVideoId,
            startSeconds: startSeconds || 0
          });
          if (typeof this.player.playVideo === 'function') {
            this.player.playVideo();
          }
          // Chủ động tắt phụ đề ngay khi nạp video
          this.disableSubtitles();
          setTimeout(() => this.disableSubtitles(), 600);
        }
      } else {
        if (typeof this.player.cueVideoById === 'function') {
          this.player.cueVideoById({
            videoId: cleanVideoId,
            startSeconds: startSeconds || 0
          });
          this.disableSubtitles();
        }
      }

      // Đồng bộ tải video với độ phân giải thấp nhất (tiny - 144p) sang Ambient Player
      if (this.ambientPlayer && this.isAmbientEnabled && typeof this.ambientPlayer.loadVideoById === 'function') {
        try {
          if (autoPlay) {
            this.ambientPlayer.loadVideoById({
              videoId: cleanVideoId,
              startSeconds: startSeconds || 0,
              suggestedQuality: 'tiny'
            });
            this.ambientPlayer.mute();
            this.ambientPlayer.setVolume(0);
          } else if (typeof this.ambientPlayer.cueVideoById === 'function') {
            this.ambientPlayer.cueVideoById({
              videoId: cleanVideoId,
              startSeconds: startSeconds || 0,
              suggestedQuality: 'tiny'
            });
            this.ambientPlayer.mute();
            this.ambientPlayer.setVolume(0);
          }
        } catch (e) {}
      } else if (this.isAmbientEnabled && typeof localStorage !== 'undefined') {
        const ambientId = this.getAmbientContainerId(targetContainerId);
        this.initAmbientPlayer(ambientId, cleanVideoId, startSeconds, autoPlay);
      }
    } catch (err) {
      console.warn('Lỗi khi nạp video vào player:', err);
      // Tự động thử mount lại player nếu instance cũ bị mất kết nối
      await this.mountTo(targetContainerId, cleanVideoId, startSeconds, autoPlay);
    }
  }

  /**
   * Phát video
   */
  play() {
    if (this.player && this.isReady && typeof this.player.playVideo === 'function') {
      try {
        this.player.playVideo();
      } catch (err) {
        console.warn('Lỗi khi gọi playVideo:', err);
      }
    }
    // Đồng bộ phát cho Ambient Player
    if (this.ambientPlayer && this.isAmbientEnabled && typeof this.ambientPlayer.playVideo === 'function') {
      try {
        this.ambientPlayer.playVideo();
      } catch (e) {}
    }
    this.updateAmbientBoxUI();
  }

  /**
   * Tạm dừng video
   */
  pause() {
    if (this.player && this.isReady && typeof this.player.pauseVideo === 'function') {
      try {
        this.player.pauseVideo();
      } catch (err) {
        console.warn('Lỗi khi gọi pauseVideo:', err);
      }
    }
    // Đồng bộ tạm dừng cho Ambient Player
    if (this.ambientPlayer && typeof this.ambientPlayer.pauseVideo === 'function') {
      try {
        this.ambientPlayer.pauseVideo();
      } catch (e) {}
    }
  }

  /**
   * Chuyển đổi trạng thái phát/tạm dừng
   */
  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Tua đến một thời điểm cụ thể
   * @param {number} seconds Vị trí giây cần tua tới
   * @param {boolean} [allowSeekAhead=true]
   */
  seekTo(seconds, allowSeekAhead = true) {
    if (this.player && this.isReady && typeof this.player.seekTo === 'function') {
      try {
        const sec = Math.max(0, seconds || 0);
        this.player.seekTo(sec, allowSeekAhead);
        this.broadcastTime();
      } catch (err) {
        console.warn('Lỗi khi tua video:', err);
      }
    }
    // Đồng bộ tua cho Ambient Player
    if (this.ambientPlayer && this.isAmbientEnabled && typeof this.ambientPlayer.seekTo === 'function') {
      try {
        const sec = Math.max(0, seconds || 0);
        this.ambientPlayer.seekTo(sec, allowSeekAhead);
      } catch (e) {}
    }
  }

  /**
   * Tua thời gian tương đối (lùi lại hoặc tiến lên số giây nhất định, ví dụ +10s hoặc -10s)
   * @param {number} deltaSeconds Số giây cần dịch chuyển
   * @returns {number} Thời điểm sau khi tua (giây)
   */
  seekRelative(deltaSeconds) {
    if (!this.player || !this.isReady) return 0;
    const curr = this.getCurrentTime();
    const dur = this.getDuration();
    let target = curr + deltaSeconds;
    if (target < 0) target = 0;
    if (dur > 0 && target > dur) target = dur;
    this.seekTo(target, true);
    return target;
  }

  /**
   * Điều chỉnh tốc độ phát (0.5x, 0.75x, 1x, 1.25x, 1.5x, 2x)
   * @param {number} rate Tốc độ phát
   */
  setPlaybackRate(rate) {
    const validRate = Number(rate);
    if (isNaN(validRate) || validRate <= 0) return;

    this.playbackRate = validRate;
    if (this.player && this.isReady && typeof this.player.setPlaybackRate === 'function') {
      try {
        this.player.setPlaybackRate(validRate);
      } catch (err) {
        console.warn('Lỗi khi setPlaybackRate:', err);
      }
    }
    // Đồng bộ tốc độ phát cho Ambient Player
    if (this.ambientPlayer && typeof this.ambientPlayer.setPlaybackRate === 'function') {
      try {
        this.ambientPlayer.setPlaybackRate(validRate);
      } catch (e) {}
    }
  }

  /**
   * Điều chỉnh âm lượng (0 - 100)
   * @param {number} vol Mức âm lượng
   */
  setVolume(vol) {
    const validVol = Math.max(0, Math.min(100, Number(vol) || 0));
    this.volume = validVol;
    if (this.player && this.isReady && typeof this.player.setVolume === 'function') {
      try {
        this.player.setVolume(validVol);
        if (validVol > 0 && this.isMuted) {
          this.unMute();
        }
      } catch (err) {
        console.warn('Lỗi khi setVolume:', err);
      }
    }
  }

  /**
   * Tắt tiếng
   */
  mute() {
    this.isMuted = true;
    if (this.player && this.isReady && typeof this.player.mute === 'function') {
      try {
        this.player.mute();
      } catch (err) {
        console.warn('Lỗi khi mute:', err);
      }
    }
  }

  /**
   * Bật lại tiếng
   */
  unMute() {
    this.isMuted = false;
    if (this.player && this.isReady && typeof this.player.unMute === 'function') {
      try {
        this.player.unMute();
      } catch (err) {
        console.warn('Lỗi khi unMute:', err);
      }
    }
  }

  /**
   * Lấy thời gian phát hiện tại (giây)
   * @returns {number}
   */
  getCurrentTime() {
    if (this.player && this.isReady && typeof this.player.getCurrentTime === 'function') {
      try {
        return this.player.getCurrentTime() || 0;
      } catch (e) {
        return 0;
      }
    }
    return 0;
  }

  /**
   * Lấy tổng thời lượng video (giây)
   * @returns {number}
   */
  getDuration() {
    if (this.player && this.isReady && typeof this.player.getDuration === 'function') {
      try {
        return this.player.getDuration() || 0;
      } catch (e) {
        return 0;
      }
    }
    return 0;
  }

  /**
   * Lấy title video hiện tại từ YouTube IFrame Player API.
   * @returns {string}
   */
  getVideoTitle() {
    if (!this.player || !this.isReady || typeof this.player.getVideoData !== 'function') {
      return '';
    }

    try {
      return this.player.getVideoData()?.title?.trim() || '';
    } catch (err) {
      console.warn('Không thể đọc title video từ YouTube Player:', err);
      return '';
    }
  }

  /**
   * Chuyển đổi và gắn player sang một container DOM mới khi người dùng chuyển giao diện (CapCut <-> Canva)
   * Có cơ chế tái sử dụng thông minh khi cùng container, tránh destroy iframe gây đứt kết nối API
   * @param {string} targetContainerId ID phần tử DOM đích
   * @param {string|null} [targetVideoId=null] Mã video chỉ định nạp
   * @param {number} [startSeconds=0] Vị trí giây bắt đầu
   * @param {boolean} [autoPlay=false] Tự động phát
   * @returns {Promise<void>}
   */
  async mountTo(targetContainerId, targetVideoId = null, startSeconds = 0, autoPlay = false) {
    if (!targetContainerId) return;

    const savedVideoId = targetVideoId || this.currentVideoId;
    const savedTime = targetVideoId ? startSeconds : this.getCurrentTime();
    const wasPlaying = targetVideoId ? autoPlay : this.isPlaying;

    // 1. KIỂM TRA THÔNG MINH: Nếu player đã sẵn sàng và đã nằm trong đúng container mục tiêu
    const isSameContainer = Boolean(
      this.currentContainerId &&
      (this.currentContainerId === targetContainerId || this.currentContainerId.startsWith(`${targetContainerId}-`))
    );
    const hasValidIframe = Boolean(
      document.querySelector(`#${targetContainerId} iframe`)
    );

    if (this.player && this.isReady && isSameContainer && hasValidIframe) {
      console.log(`[VideoEngine] Tái sử dụng player hiện tại tại #${targetContainerId} | Video: ${savedVideoId || 'hiện tại'}`);
      if (savedVideoId) {
        if (this.currentVideoId === savedVideoId) {
          // Cùng video: Chỉ cần tua tới vị trí yêu cầu và tiếp tục phát nếu cần
          if (Number.isFinite(savedTime) && savedTime > 0) {
            this.seekTo(savedTime, true);
          }
          if (wasPlaying) {
            this.play();
          }
          return;
        } else {
          // Khác video: Nạp video mới bằng API chính thức loadVideo, không cần reload iframe
          await this.loadVideo(savedVideoId, savedTime, wasPlaying);
          return;
        }
      }
      return;
    }

    // 2. KHÓA CHỐNG XUNG ĐỘT (Concurrency Guard): Bỏ qua nếu đang có tiến trình mountTo khác chạy
    if (this.isMounting) {
      console.warn(`[VideoEngine] Tiến trình mountTo đang bận, bỏ qua lệnh mount trùng lặp vào #${targetContainerId}`);
      return;
    }
    this.isMounting = true;

    try {
      console.log(`[VideoEngine] Chuyển Player sang container: #${targetContainerId} | Video: ${savedVideoId || 'trống'}`);

      // Dừng ticker hiện tại
      this.stopTicker();

      // Hủy đối tượng player cũ để giải phóng tài nguyên iframe khi chuyển container
      if (this.player && typeof this.player.destroy === 'function') {
        try {
          this.player.destroy();
        } catch (e) {
          console.warn('Lỗi khi destroy player cũ:', e);
        }
        this.player = null;
        this.isReady = false;
      }

      // Hủy đối tượng Ambient Player cũ để giải phóng tài nguyên iframe
      this.destroyAmbientPlayer();

      // Đảm bảo DOM đích có sẵn phần tử con để YT.Player mount vào
      let targetEl = document.getElementById(targetContainerId);
      if (!targetEl) {
        console.warn(`Không tìm thấy phần tử DOM #${targetContainerId} để mount`);
        return;
      }

      // Xóa sạch nội dung cũ bên trong (tránh sinh lồng iframe)
      targetEl.innerHTML = '';
      const iframePlaceholder = document.createElement('div');
      const placeholderId = `${targetContainerId}-yt-inner`;
      iframePlaceholder.id = placeholderId;
      iframePlaceholder.style.width = '100%';
      iframePlaceholder.style.height = '100%';
      targetEl.appendChild(iframePlaceholder);

      // Khởi tạo player tại container mới nếu đã có videoId
      if (savedVideoId) {
        await this.initPlayer(placeholderId, savedVideoId, savedTime, wasPlaying);
      } else {
        this.currentContainerId = targetContainerId;
      }
    } finally {
      this.isMounting = false;
    }
  }

  /**
   * Tắt hoàn toàn phụ đề (Closed Captions / Subtitles) của video YouTube
   * Kết hợp cả API Iframe chính thức và cơ chế postMessage trực tiếp vào iframe
   */
  disableSubtitles() {
    // Bước 1: Kiểm tra rỗng và trạng thái sẵn sàng của đối tượng player
    if (!this.player) return;

    // Bước 2: Thử gọi các hàm gỡ bỏ và tắt track phụ đề chính thức của YouTube Iframe API
    try {
      if (typeof this.player.unloadModule === 'function') {
        this.player.unloadModule('captions');
        this.player.unloadModule('cc');
      }
      if (typeof this.player.setOption === 'function') {
        this.player.setOption('captions', 'track', {});
        this.player.setOption('captions', 'reload', true);
        this.player.setOption('cc', 'track', {});
      }
    } catch (e) {
      // Bỏ qua lỗi nếu player chưa tải xong module phụ đề
    }

    // Bước 3: Dự phòng gửi thông điệp trực tiếp (postMessage) tới cửa sổ iframe YouTube
    try {
      const iframe = (typeof this.player.getIframe === 'function' ? this.player.getIframe() : null)
        || document.querySelector(`#${this.currentContainerId} iframe`)
        || document.getElementById(this.currentContainerId);

      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: 'unloadModule',
          args: ['captions']
        }), '*');

        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: 'unloadModule',
          args: ['cc']
        }), '*');

        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: 'setOption',
          args: ['captions', 'track', {}]
        }), '*');
      }
    } catch (e) {
      // Bỏ qua nếu có giới hạn cross-origin
    }
  }

  /**
   * BOSS KEY KHẨN CẤP: Dừng video và tắt tiếng ngay lập tức trong 0 mili-giây
   */
  emergencyMuteAndPause() {
    this.stopTicker();
    if (this.player && this.isReady) {
      try {
        if (typeof this.player.mute === 'function') this.player.mute();
        if (typeof this.player.pauseVideo === 'function') this.player.pauseVideo();
      } catch (e) {
        // Bỏ qua lỗi khi khẩn cấp
      }
    }
    // Dừng và tắt tiếng ngay lập tức cả Ambient Player phụ
    if (this.ambientPlayer) {
      try {
        if (typeof this.ambientPlayer.mute === 'function') this.ambientPlayer.mute();
        if (typeof this.ambientPlayer.pauseVideo === 'function') this.ambientPlayer.pauseVideo();
      } catch (e) {}
    }
    this.isPlaying = false;
    this.updateAmbientBoxUI();
  }

  /**
   * Lấy ID container của iframe phụ Ambient Light tương ứng với container chính
   * @param {string} [targetContainerId=null]
   * @returns {string}
   */
  getAmbientContainerId(targetContainerId = null) {
    const cid = (targetContainerId || this.currentContainerId || '').toLowerCase();
    if (cid.includes('canva') || cid.includes('cv')) {
      return 'cv-ambient-iframe';
    }
    return 'cc-ambient-iframe';
  }

  /**
   * Khởi tạo YouTube Player phụ phục vụ hiệu ứng ánh sáng môi trường Ambient Light (Double-Player)
   * IFrame phụ được cấu hình chất lượng thấp nhất (vq: tiny - 144p) và tắt tiếng 100% để tối ưu băng thông và GPU
   * @param {string} containerId ID phần tử chứa iframe phụ (cc-ambient-iframe / cv-ambient-iframe)
   * @param {string} videoId Mã video YouTube
   * @param {number} [startSeconds=0] Giây bắt đầu
   * @param {boolean} [autoPlay=false] Trạng thái phát
   * @returns {Promise<void>}
   */
  async initAmbientPlayer(containerId, videoId, startSeconds = 0, autoPlay = false) {
    if (!this.isAmbientEnabled || !videoId) {
      this.destroyAmbientPlayer();
      this.updateAmbientBoxUI();
      return;
    }

    if (typeof window === 'undefined' || !window.YT || !window.YT.Player) {
      return;
    }

    const targetEl = document.getElementById(containerId);
    if (!targetEl) return;

    // Hủy instance cũ nếu có
    this.destroyAmbientPlayer();

    // Dọn sạch ruột và tạo placeholder con
    targetEl.innerHTML = '';
    const placeholder = document.createElement('div');
    const placeholderId = `${containerId}-yt-inner`;
    placeholder.id = placeholderId;
    placeholder.style.width = '100%';
    placeholder.style.height = '100%';
    targetEl.appendChild(placeholder);

    try {
      this.currentAmbientContainerId = containerId;
      const initialTime = Math.max(0, Number(startSeconds) || 0);

      this.ambientPlayer = new window.YT.Player(placeholderId, {
        height: '100%',
        width: '100%',
        videoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          start: Math.floor(initialTime),
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          cc_load_policy: 0,
          playsinline: 1,
          mute: 1,      // Tắt tiếng ngay từ thông số nạp
          vq: 'tiny'    // Yêu cầu chất lượng thấp nhất (144p) để siêu nhẹ và tránh lag
        },
        events: {
          onReady: (event) => {
            try {
              event.target.mute();
              event.target.setVolume(0);
              if (typeof event.target.setPlaybackQuality === 'function') {
                event.target.setPlaybackQuality('tiny');
              }
              this.isAmbientReady = true;
              if (initialTime > 0) {
                event.target.seekTo(initialTime, true);
              }
              if (autoPlay && this.isPlaying) {
                event.target.playVideo();
              }
              this.updateAmbientBoxUI();
            } catch (e) {}
          },
          onStateChange: (event) => {
            // Khi Ambient Player phát, luôn đảm bảo tắt tiếng và duy trì độ phân giải thấp
            if (event.data === 1) {
              try {
                event.target.mute();
                event.target.setVolume(0);
                if (typeof event.target.setPlaybackQuality === 'function') {
                  event.target.setPlaybackQuality('tiny');
                }
              } catch (e) {}
            }
          }
        }
      });
    } catch (err) {
      console.warn('[VideoEngine] Lỗi khởi tạo Ambient Player:', err);
    }
  }

  /**
   * Hủy bỏ đối tượng Ambient Player để giải phóng tài nguyên bộ nhớ
   */
  destroyAmbientPlayer() {
    if (this.ambientPlayer && typeof this.ambientPlayer.destroy === 'function') {
      try {
        this.ambientPlayer.destroy();
      } catch (e) {}
    }
    this.ambientPlayer = null;
    this.isAmbientReady = false;
  }

  /**
   * Cập nhật class hiển thị của các khung viền Ambient Light trên DOM
   */
  updateAmbientBoxUI() {
    if (typeof document === 'undefined') return;

    const boxes = [
      document.getElementById('cc-ambient-box'),
      document.getElementById('cv-ambient-box')
    ];

    boxes.forEach(box => {
      if (!box) return;
      if (!this.isAmbientEnabled) {
        box.classList.add('disabled');
        box.classList.remove('active');
      } else {
        box.classList.remove('disabled');
        box.classList.add('active');
      }
    });
  }

  /**
   * Bật/Tắt hiệu ứng ánh sáng môi trường Ambient Light
   * @returns {boolean} Trạng thái sau khi chuyển đổi
   */
  toggleAmbientLight() {
    return this.setAmbientLightEnabled(!this.isAmbientEnabled);
  }

  /**
   * Thiết lập trạng thái Bật/Tắt Ambient Light và lưu vào localStorage
   * @param {boolean} enabled
   * @returns {boolean}
   */
  setAmbientLightEnabled(enabled) {
    this.isAmbientEnabled = Boolean(enabled);

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fake_video_ambient_light', this.isAmbientEnabled ? 'true' : 'false');
      }
    } catch (e) {}

    if (this.isAmbientEnabled) {
      // Nếu bật lại: Khởi tạo player phụ nếu chưa có hoặc phát tiếp
      const targetAmbientId = this.getAmbientContainerId();
      if (!this.ambientPlayer && this.currentVideoId) {
        this.initAmbientPlayer(targetAmbientId, this.currentVideoId, this.getCurrentTime(), this.isPlaying);
      } else if (this.ambientPlayer) {
        try {
          this.ambientPlayer.seekTo(this.getCurrentTime(), true);
          if (this.isPlaying) {
            this.ambientPlayer.playVideo();
          }
        } catch (e) {}
      }
    } else {
      // Nếu tắt: Tạm dừng player phụ ngay để tiết kiệm tài nguyên
      if (this.ambientPlayer && typeof this.ambientPlayer.pauseVideo === 'function') {
        try { this.ambientPlayer.pauseVideo(); } catch (e) {}
      }
    }

    this.updateAmbientBoxUI();
    this.emit('ambientChange', { enabled: this.isAmbientEnabled });
    return this.isAmbientEnabled;
  }
}
